/**
 * ExecBriefingPipeline — parallel agent grid showing 4 section agents
 * that light up independently as each completes.
 * Driven by partial_sections data from the polling response.
 */

import React, { useState, useEffect, useRef } from 'react';

const SECTIONS = [
  { id: 'executive_summary', label: 'Executive Summary', icon: '📊', color: '#0073bb' },
  { id: 'swo_analysis', label: 'SWO Analysis', icon: '💪', color: '#16a085' },
  { id: 'key_financials', label: 'Key Financials', icon: '💰', color: '#2c6fba' },
  { id: 'risk_overview', label: 'Risk Overview', icon: '⚠️', color: '#e67e22' },
];

export default function ExecBriefingPipeline({ generationProgress, generationStage, isGenerating, isComplete, partialSections }) {
  const [elapsed, setElapsed] = useState(0);
  const startTime = useRef(null);

  // Determine which sections are complete based on partialSections prop
  const completedSections = partialSections ? Object.keys(partialSections) : [];
  const completedCount = completedSections.length;

  // Start timer when generation begins
  useEffect(() => {
    if (isGenerating && !startTime.current) {
      startTime.current = Date.now();
    }
    if (isComplete) {
      startTime.current = null;
    }
  }, [isGenerating, isComplete]);

  // Tick elapsed time
  useEffect(() => {
    if (!isGenerating) return;
    const interval = setInterval(() => {
      if (startTime.current) {
        setElapsed(((Date.now() - startTime.current) / 1000).toFixed(1));
      }
    }, 100);
    return () => clearInterval(interval);
  }, [isGenerating]);

  if (!isGenerating && !isComplete) return null;

  const progressPct = isComplete ? 100 : (completedCount / 4) * 100;

  return (
    <div style={{
      padding: '20px 24px',
      borderRadius: 10,
      border: '1px solid #1d8348',
      background: 'linear-gradient(135deg, #0a1628 0%, #0f1f35 100%)',
      boxShadow: '0 4px 20px rgba(9, 114, 211, 0.1)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>🤖</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>Storytelling Agent</span>
          {isGenerating && !isComplete && (
            <span style={{
              fontSize: 11,
              color: '#0972d3',
              background: 'rgba(9, 114, 211, 0.15)',
              border: '1px solid #0972d3',
              borderRadius: 4,
              padding: '2px 10px',
              fontWeight: 500,
            }}>
              {completedCount}/4 Parallel
            </span>
          )}
          {isComplete && (
            <span style={{
              fontSize: 11,
              color: '#1d8348',
              background: 'rgba(29, 131, 72, 0.15)',
              border: '1px solid #1d8348',
              borderRadius: 4,
              padding: '2px 10px',
              fontWeight: 500,
            }}>
              ✓ Complete
            </span>
          )}
        </div>
        {/* Elapsed timer */}
        <span style={{ fontSize: 13, color: '#8d99a8', fontFamily: 'monospace' }}>
          {elapsed}s
        </span>
      </div>

      {/* Progress bar */}
      <div style={{
        height: 4,
        background: '#1a2332',
        borderRadius: 2,
        marginBottom: 18,
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${progressPct}%`,
          background: isComplete
            ? 'linear-gradient(90deg, #1d8348, #27ae60)'
            : 'linear-gradient(90deg, #0972d3, #44b9d6)',
          borderRadius: 2,
          transition: 'width 0.5s ease',
        }} />
      </div>

      {/* 2x2 Agent Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
      }}>
        {SECTIONS.map((section) => {
          const isDone = isComplete || completedSections.includes(section.id);
          const isActive = isGenerating && !isDone;

          return (
            <div
              key={section.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 14px',
                borderRadius: 8,
                border: `1px solid ${isDone ? section.color : '#2a3544'}`,
                background: isDone
                  ? `${section.color}15`
                  : 'rgba(26, 35, 50, 0.6)',
                transition: 'all 0.4s ease',
                opacity: isDone ? 1 : 0.6,
              }}
            >
              {/* Status icon */}
              <div style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                flexShrink: 0,
                background: isDone ? section.color : '#2a3544',
                color: '#fff',
                boxShadow: isDone ? `0 0 10px ${section.color}60` : 'none',
                transition: 'all 0.4s ease',
              }}>
                {isDone ? '✓' : section.icon}
              </div>

              {/* Label */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{
                  fontSize: 13,
                  fontWeight: isDone ? 600 : 400,
                  color: isDone ? '#fff' : '#8d99a8',
                  transition: 'color 0.3s',
                }}>
                  {section.label}
                </span>
              </div>

              {/* Status indicator */}
              {isActive && (
                <span style={{
                  fontSize: 10,
                  color: '#0972d3',
                  fontWeight: 500,
                  animation: 'pulse 1.5s infinite',
                }}>
                  ⟳
                </span>
              )}
              {isDone && (
                <span style={{
                  fontSize: 10,
                  color: section.color,
                  fontWeight: 600,
                }}>
                  done
                </span>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
